import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useGlobalPopup } from "~/components/GlobalPopupProvider";
import LoginPromptModal from "~/components/elements/products/modules/LoginPromptModal";
import ReviewPhotoTips from "./ReviewPhotoTips";

const ratingLabels = ["Schlecht", "OK", "Durchschnittlich", "Gut", "Hervorragend"];
const ACCENT = "rgb(45, 70, 105)";
const API_URL = process.env.NEXT_PUBLIC_API_URL;

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const REVIEW_LOGIN_BENEFITS = [
  { icon: "check", text: "Bewerten, was du bei uns gekauft hast" },
  { icon: "star", text: "Anderen Kunden bei der Kaufentscheidung helfen" },
  { icon: "gift", text: "Am Gewinnspiel teilnehmen" },
];

const fallbackPopup = ({ title, detail }) => window.alert(`${title}\n${detail || ""}`);
export const useSafePopup = () => {
  let ctx = null;
  try {
    ctx = useGlobalPopup();
  } catch (e) {
    ctx = null;
  }
  return ctx?.showPopup || fallbackPopup;
};

export const reviewThanksDetail = (result) =>
  result?.nicknameSaved && result?.nickname
    ? `Dein Spitzname „${result.nickname}“ wurde in deinem Kundenkonto gespeichert und gilt ab jetzt für alle deine Bewertungen. Deine Bewertung wird nach einer kurzen Prüfung veröffentlicht.`
    : "Sie wird nach einer kurzen Prüfung veröffentlicht.";

const fieldsetStyle = { border: "none", marginBottom: 22, padding: 0 };
const labelStyle = { fontWeight: 600, fontSize: 14, display: "block", marginBottom: 8, color: "#1e293b" };

export const ProductReviewModal = ({
  productId,
  orderId,
  productName,
  productImage,
  onClose,
  onSubmitted,
  initialRating = null,
  savedNickname = null,
}) => {
  const starsCount = 5;
  const [rating, setRating] = useState(initialRating);
  const [hoveredRating, setHoveredRating] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [reviewTitle, setReviewTitle] = useState("");
  const [nickname, setNickname] = useState(savedNickname || "");
  const [nicknameStatus, setNicknameStatus] = useState({ state: "idle", message: "" });
  const [agreeContest, setAgreeContest] = useState(null);
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const [submitHover, setSubmitHover] = useState(false);

  const fileInputRef = useRef(null);
  const imagesRef = useRef([]);

  const displayedRating = hoveredRating !== null ? hoveredRating : rating;
  const imagesFull = images.length >= MAX_IMAGES;
  const dropDisabled = submitting || imagesFull;

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.url));
    },
    []
  );

  useEffect(() => {
    if (savedNickname) return undefined;

    const value = nickname.trim();
    if (value.length < 2) {
      setNicknameStatus({ state: "idle", message: "" });
      return undefined;
    }

    const token = typeof window !== "undefined" ? localStorage.getItem("jwt") : null;
    if (!token) return undefined;

    let cancelled = false;
    setNicknameStatus({ state: "checking", message: "" });

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_URL}/product-reviews/nickname-check?nickname=${encodeURIComponent(value)}`,
          { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" }
        );
        const json = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok || !json) {
          setNicknameStatus({ state: "idle", message: "" });
          return;
        }
        if (json.available) {
          setNicknameStatus({ state: "available", message: "Dieser Spitzname ist verfügbar." });
        } else {
          setNicknameStatus({ state: "unavailable", message: json.message || "Dieser Spitzname ist nicht verfügbar." });
        }
      } catch (err) {
        if (!cancelled) setNicknameStatus({ state: "idle", message: "" });
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [nickname, savedNickname]);

  const triggerShake = () => {
    setShakeError(true);
    setTimeout(() => setShakeError(false), 600);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") onClose();
  };

  const addFiles = (picked) => {
    if (!picked.length) return;

    const next = [...images];
    let message = "";

    for (const file of picked) {
      if (next.length >= MAX_IMAGES) {
        message = `Du kannst maximal ${MAX_IMAGES} Bilder hochladen.`;
        break;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        message = "Erlaubt sind nur JPG-, PNG- und WebP-Bilder.";
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        message = "Jedes Bild darf höchstens 5 MB groß sein.";
        continue;
      }
      const id = `${file.name}-${file.size}-${file.lastModified}`;
      if (next.some((image) => image.id === id)) continue;
      next.push({ id, file, url: URL.createObjectURL(file) });
    }

    setImages(next);
    setImageError(message);
  };

  const handleInputChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    addFiles(picked);
  };

  const openFileDialog = () => {
    if (dropDisabled) return;
    fileInputRef.current?.click();
  };

  const handleDropZoneKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openFileDialog();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dropDisabled) setDragActive(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (dropDisabled) return;
    addFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleRemoveImage = (id) => {
    const target = images.find((image) => image.id === id);
    if (target) URL.revokeObjectURL(target.url);
    setImages(images.filter((image) => image.id !== id));
    setImageError("");
  };

  const validate = () => {
    const newErrors = {};
    if (!rating) newErrors.rating = "Gesamtbewertung ist erforderlich.";
    if (!reviewText || reviewText.trim().length < 50)
      newErrors.reviewText = "Die Bewertung muss mindestens 50 Zeichen enthalten.";
    if (!reviewTitle || reviewTitle.trim().length < 3)
      newErrors.reviewTitle = "Bewertungstitel ist erforderlich (mind. 3 Zeichen).";
    if (!nickname || nickname.trim().length < 2)
      newErrors.nickname = "Spitzname ist erforderlich (mind. 2 Zeichen).";
    else if (!savedNickname && nicknameStatus.state === "unavailable")
      newErrors.nickname = nicknameStatus.message;
    if (agreeContest === null) newErrors.agreeContest = "Bitte Teilnahmebedingungen bestätigen.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setServerError("");
    if (!validate()) {
      triggerShake();
      return;
    }

    const token = typeof window !== "undefined" ? localStorage.getItem("jwt") : null;
    if (!token) {
      setServerError("Bitte logge dich ein, um eine Bewertung abzugeben.");
      return;
    }

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        product: productId,
        order: orderId,
        rating,
        title: reviewTitle,
        content: reviewText,
        nickname,
        contest: agreeContest ? "yes" : "no",
      })
    );
    images.forEach((image) => formData.append("images", image.file, image.file.name));

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/product-reviews`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setServerError(json?.error?.message || "Die Bewertung konnte nicht gesendet werden.");
        triggerShake();
        return;
      }
      if (onSubmitted) {
        onSubmitted({
          nicknameSaved: Boolean(json?.data?.nicknameSaved),
          nickname: json?.data?.nickname || null,
        });
      }
      onClose();
    } catch (err) {
      setServerError("Netzwerkfehler. Bitte versuche es später erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          50% { transform: translateX(5px); }
          75% { transform: translateX(-5px); }
        }
        .shake { animation: shake 0.6s ease-in-out; font-weight: 600; color: #b3261e; }
        .error-message { font-weight: 600; color: #b3261e; margin-top: 6px; font-size: 13px; }
        .rw-ok { font-weight: 600; color: #1f7a3d; margin-top: 6px; font-size: 13px; }
        .rw-hint { color: #6b7280; margin: 6px 0 0; font-size: 13px; line-height: 1.5; }
        .rw-field {
          width: 100%;
          padding: 11px 14px;
          box-sizing: border-box;
          font-size: 14px;
          font-family: inherit;
          border: 1px solid #d7dbe0;
          border-radius: 10px;
          color: #1e293b;
          background-color: #fbfbfc;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
        }
        .rw-field::placeholder { color: #9aa1ab; }
        .rw-field:focus {
          outline: none;
          border-color: ${ACCENT};
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(45, 70, 105, 0.12);
        }
        .rw-field:disabled {
          background-color: #f1f2f4;
          border-color: #d7dbe0;
          box-shadow: none;
          color: #4b5563;
          -webkit-text-fill-color: #4b5563;
          opacity: 1;
          cursor: default;
          pointer-events: none;
          user-select: none;
        }
        .rw-radio { accent-color: ${ACCENT}; width: 16px; height: 16px; cursor: pointer; }
        .rw-close:hover { background-color: #f1f2f4; }
        .rw-link { color: ${ACCENT}; text-decoration: underline; }
        .rw-link-btn {
          padding: 0;
          border: none;
          background: none;
          font: inherit;
          font-weight: 600;
          color: ${ACCENT};
          text-decoration: underline;
          cursor: pointer;
        }
        .rw-drop {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 26px 16px;
          border: 1.5px dashed #c3c9d1;
          border-radius: 12px;
          background-color: #fbfbfc;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }
        .rw-drop:hover,
        .rw-drop:focus-visible,
        .rw-drop.is-drag {
          outline: none;
          border-color: ${ACCENT};
          background-color: #f5f8fc;
        }
        .rw-drop.is-disabled { opacity: 0.6; cursor: default; pointer-events: none; }
        .rw-drop-title { font-size: 15px; font-weight: 600; color: #1e293b; margin-top: 6px; }
        .rw-drop-sub { font-size: 13px; color: #6b7280; }
        .rw-thumbs { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 12px; }
        .rw-thumb { width: 84px; }
        .rw-thumb img {
          display: block;
          width: 84px;
          height: 84px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #eef0f2;
        }
        .rw-thumb-remove {
          margin-top: 4px;
          width: 100%;
          padding: 4px 0;
          border: none;
          background: none;
          color: #b3261e;
          font-family: inherit;
          font-size: 12px;
          font-weight: 600;
          text-decoration: underline;
          cursor: pointer;
        }
        .rw-thumb-remove:disabled { opacity: 0.6; cursor: default; }
      `}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Meine Bewertung für ${productName}`}
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(15, 23, 42, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 2147483647,
          color: "#2f3134",
          padding: 16,
          boxSizing: "border-box",
          overscrollBehavior: "contain",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            maxWidth: 600,
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 16,
            padding: 32,
            position: "relative",
            boxSizing: "border-box",
            boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
            overscrollBehavior: "contain",
          }}
        >
          <button
            className="rw-close"
            onClick={onClose}
            aria-label="Schließen"
            type="button"
            style={{
              position: "absolute",
              top: 18,
              right: 18,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 8,
              borderRadius: 8,
              display: "flex",
              transition: "background-color 0.15s ease",
            }}
          >
            <svg width={18} height={18} viewBox="0 0 21 21" fill="none" aria-hidden="true">
              <path
                d="M2.03849 0.769828L10.5 8.89086L18.9616 0.547721C19.262 0.25152 19.9743 0.473626 20.2747 0.769828C20.5751 1.06603 20.5751 1.76844 20.2747 2.06464L12.0385 10.4078L20.2747 18.751C20.5751 19.0472 20.5751 19.7496 20.2747 20.0458C19.9743 20.342 19.262 20.5642 18.9616 20.268L10.5 11.9247L2.03849 20.268C1.73808 20.5642 1.02573 20.342 0.72533 20.0458C0.424927 19.7496 0.424927 18.7989 0.72533 18.5027L8.96156 10.4078L0.725275 2.06457C0.424872 1.76837 0.424927 1.06603 0.72533 0.769828C1.02573 0.473626 1.73808 0.473626 2.03849 0.769828Z"
                fill="#2F3134"
              />
            </svg>
          </button>

          <div style={{ display: "flex", gap: 18, alignItems: "center", marginBottom: 28 }}>
            <img
              src={productImage}
              alt={`Bild von ${productName}`}
              style={{ width: 88, height: 88, borderRadius: 12, objectFit: "cover", border: "1px solid #eef0f2" }}
            />
            <div>
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Meine Bewertung
              </p>
              <h1 style={{ fontSize: 19, margin: "2px 0 0", fontWeight: 700, color: "#1e293b" }}>{productName}</h1>
            </div>
          </div>

          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 26 }}>Pflichtfelder sind mit * gekennzeichnet</p>

          <form onSubmit={handleSubmit} noValidate>
            <fieldset style={fieldsetStyle}>
              <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: "#1e293b" }}>Gesamtbewertung*</legend>
              <div role="radiogroup" aria-label="Gesamtbewertung" style={{ display: "flex", gap: 6 }} onMouseLeave={() => setHoveredRating(null)}>
                {[...Array(starsCount)].map((_, i) => {
                  const starNumber = i + 1;
                  const checked = displayedRating !== null && starNumber <= displayedRating;
                  const isSelected = rating === starNumber;
                  return (
                    <label
                      key={starNumber}
                      htmlFor={`star-${starNumber}`}
                      style={{
                        cursor: "pointer",
                        display: "inline-flex",
                        color: checked ? ACCENT : "#c7ccd3",
                        transition: "color 0.12s ease, transform 0.12s ease",
                        transform: checked ? "scale(1.05)" : "scale(1)",
                      }}
                      aria-checked={isSelected}
                      role="radio"
                      tabIndex={isSelected ? "0" : "-1"}
                      onMouseEnter={() => setHoveredRating(starNumber)}
                      onFocus={() => setHoveredRating(starNumber)}
                      onBlur={() => setHoveredRating(null)}
                    >
                      <input
                        style={{ display: "none" }}
                        type="radio"
                        id={`star-${starNumber}`}
                        name="rating"
                        value={starNumber}
                        onChange={() => setRating(starNumber)}
                      />
                      <svg
                        width={34}
                        height={34}
                        viewBox="0 0 34 34"
                        fill={checked ? ACCENT : "none"}
                        stroke={checked ? ACCENT : "#c7ccd3"}
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <path d="M16.307 1.765 L20.838 11.417 L31.059 12.979 L23.660 20.562 L25.398 31.212 L16.308 26.187 L7.218 31.212 L8.955 20.562 L1.556 12.979 L11.777 11.417 L16.307 1.765 Z" />
                      </svg>
                    </label>
                  );
                })}
              </div>

              <div style={{ marginTop: 10, fontSize: 13, minHeight: 18, color: "#4b5563", fontWeight: 500 }} aria-live="polite">
                {displayedRating !== null &&
                  `${displayedRating} von 5 Sternen ausgewählt. Das Produkt ist ${ratingLabels[displayedRating - 1]}.`}
              </div>
              {errors.rating && <p className={`error-message ${shakeError ? "shake" : ""}`}>{errors.rating}</p>}
            </fieldset>

            <fieldset style={fieldsetStyle}>
              <label htmlFor="review-text" style={labelStyle}>Bewertung* (mindestens 50 Zeichen)</label>
              <textarea
                id="review-text"
                className="rw-field"
                placeholder="Beispiel: Vor einem Monat gekauft und keine Sekunde bereut ..."
                aria-required="true"
                rows={4}
                maxLength={2000}
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              />
              {errors.reviewText && <p className={`error-message ${shakeError ? "shake" : ""}`}>{errors.reviewText}</p>}
            </fieldset>

            <fieldset style={fieldsetStyle}>
              <label htmlFor="review-title" style={labelStyle}>Bewertungstitel*</label>
              <input
                id="review-title"
                type="text"
                className="rw-field"
                maxLength={50}
                placeholder="Beispiel: Tolle Extras!"
                aria-required="true"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
              />
              {errors.reviewTitle && <p className={`error-message ${shakeError ? "shake" : ""}`}>{errors.reviewTitle}</p>}
            </fieldset>

            <fieldset style={fieldsetStyle}>
              <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: "#1e293b" }}>
                Bilder (optional, maximal {MAX_IMAGES})
              </legend>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                style={{ display: "none" }}
                onChange={handleInputChange}
                disabled={dropDisabled}
              />

              {images.length > 0 && (
                <div className="rw-thumbs">
                  {images.map((image, index) => (
                    <div className="rw-thumb" key={image.id}>
                      <img src={image.url} alt={`Ausgewähltes Bild ${index + 1}`} />
                      <button
                        type="button"
                        className="rw-thumb-remove"
                        onClick={() => handleRemoveImage(image.id)}
                        disabled={submitting}
                      >
                        Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!imagesFull && (
                <div
                  role="button"
                  tabIndex={dropDisabled ? -1 : 0}
                  aria-label="Bilder hochladen: Dateien hierher ziehen oder klicken, um sie auszuwählen"
                  aria-disabled={dropDisabled}
                  className={`rw-drop${dragActive ? " is-drag" : ""}${dropDisabled ? " is-disabled" : ""}`}
                  onClick={openFileDialog}
                  onKeyDown={handleDropZoneKeyDown}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 8h3l1.6-2h6.8L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
                    <circle cx="12" cy="13.5" r="3.4" />
                  </svg>
                  <span className="rw-drop-title">
                    {images.length === 0 ? "Zieh deine Fotos hier rein" : "Zieh weitere Fotos hier rein"}
                  </span>
                  <span className="rw-drop-sub">oder klicke und lade Bilder hoch</span>
                </div>
              )}

              {imageError && <p className="error-message">{imageError}</p>}
              <p className="rw-hint">
                Füge bis zu {MAX_IMAGES} Bilder (JPG, PNG oder WebP, max. 5 MB pro Bild) per Upload oder Drag &amp; Drop hinzu.
                Für bessere Ergebnisse helfen dir unsere{" "}
                <button type="button" className="rw-link-btn" onClick={() => setTipsOpen(true)}>
                  Fototipps
                </button>
                .
              </p>
              <p className="rw-hint">
                Die Bilder werden vor der Veröffentlichung von uns geprüft. Bitte lade keine Bilder hoch, auf denen
                Personen oder persönliche Daten zu sehen sind.
              </p>
            </fieldset>

            <fieldset style={fieldsetStyle}>
              <label htmlFor="nickname" style={labelStyle}>Spitzname - dieser wird für Besucher sichtbar angezeigt*</label>
              <input
                id="nickname"
                type="text"
                className="rw-field"
                maxLength={30}
                placeholder="Beispiel: bob27"
                aria-required="true"
                autoComplete="nickname"
                value={nickname}
                disabled={Boolean(savedNickname)}
                tabIndex={savedNickname ? -1 : 0}
                onChange={(e) => setNickname(e.target.value)}
              />
              {errors.nickname && <p className={`error-message ${shakeError ? "shake" : ""}`}>{errors.nickname}</p>}
              {!errors.nickname && !savedNickname && nicknameStatus.state === "checking" && (
                <p className="rw-hint">Spitzname wird geprüft ...</p>
              )}
              {!errors.nickname && !savedNickname && nicknameStatus.state === "available" && (
                <p className="rw-ok">{nicknameStatus.message}</p>
              )}
              {!errors.nickname && !savedNickname && nicknameStatus.state === "unavailable" && (
                <p className="error-message">{nicknameStatus.message}</p>
              )}
              <p className="rw-hint">
                {savedNickname
                  ? "Dein Spitzname ist in deinem Kundenkonto gespeichert und gilt für alle deine Bewertungen."
                  : "2 bis 30 Zeichen: Buchstaben, Zahlen, _ und -. Keine Links oder E-Mail-Adressen. Mit deiner ersten Bewertung wird dein Spitzname in deinem Kundenkonto gespeichert. Er gilt dann für alle deine Bewertungen und kann nicht mehr geändert werden."}
              </p>
            </fieldset>

            <fieldset style={fieldsetStyle}>
              <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: "#1e293b" }}>
                Teilnahme am Gewinnspiel* (Bitte Ja oder Nein auswählen)
              </legend>
              <div role="radiogroup" aria-required="true" aria-label="Teilnahme Gewinnspiel" style={{ display: "flex", gap: 20, marginBottom: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input className="rw-radio" type="radio" name="agreeContest" value="yes" checked={agreeContest === true} onChange={() => setAgreeContest(true)} />
                  Ja
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#1e293b" }}>
                  <input className="rw-radio" type="radio" name="agreeContest" value="no" checked={agreeContest === false} onChange={() => setAgreeContest(false)} />
                  Nein
                </label>
              </div>
              <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                Ich akzeptiere die{" "}
                <a className="rw-link" href="https://www.rawento.com/company/aktionen/gewinnspiele" target="_blank" rel="noopener noreferrer">
                  Teilnahmebedingungen
                </a>
              </p>
              {errors.agreeContest && <p className={`error-message ${shakeError ? "shake" : ""}`}>{errors.agreeContest}</p>}
            </fieldset>

            <p style={{ fontSize: 12, color: "#8a93a1", marginBottom: 22, lineHeight: 1.5 }}>
              Mit Klick auf „Bewertungsanfrage absenden" stimmst du den{" "}
              <a className="rw-link" href="https://www.rawento.com/company/review-bedingungen" target="_blank" rel="noopener noreferrer">
                Nutzungsbedingungen
              </a>{" "}
              zu und erklärst, die{" "}
              <a className="rw-link" href="https://www.rawento.com/company/datenschutz" target="_blank" rel="noopener noreferrer">
                Datenschutzhinweise
              </a>{" "}
              zur Kenntnis genommen zu haben. Deine Bewertung wird vor der Veröffentlichung von uns geprüft.
              Es wird die E-Mail-Adresse deines Kundenkontos verwendet; sie wird nicht angezeigt.
            </p>

            {serverError && <p className={`error-message ${shakeError ? "shake" : ""}`} style={{ marginBottom: 14 }}>{serverError}</p>}

            <button
              type="submit"
              disabled={submitting}
              aria-label="Bewertungsanfrage absenden"
              onMouseEnter={() => setSubmitHover(true)}
              onMouseLeave={() => setSubmitHover(false)}
              style={{
                backgroundColor: submitHover && !submitting ? "rgb(35, 55, 84)" : ACCENT,
                color: "white",
                border: "none",
                padding: "13px 20px",
                borderRadius: 10,
                cursor: submitting ? "wait" : "pointer",
                opacity: submitting ? 0.7 : 1,
                fontSize: 15,
                fontWeight: 600,
                width: "100%",
                transition: "background-color 0.15s ease",
              }}
            >
              {submitting ? "Wird gesendet ..." : "Bewertungsanfrage absenden"}
            </button>
          </form>
        </div>
      </div>

      {tipsOpen && <ReviewPhotoTips onClose={() => setTipsOpen(false)} />}
    </>
  );
};

export const ProductReview = ({ onStarClick }) => {
  const starsCount = 5;
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(null);

  const handleClick = (rating) => {
    if (onStarClick) onStarClick(rating);
  };

  const getButtonStyle = (idx) => {
    const isActive =
      (hoveredIndex !== null && idx <= hoveredIndex) || (focusedIndex !== null && idx <= focusedIndex);
    return {
      flexGrow: 1,
      flexBasis: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      minHeight: 46,
      maxHeight: 71,
      cursor: "pointer",
      outline: "none",
      border: `1px solid ${isActive ? ACCENT : "#d7dbe0"}`,
      transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
      backgroundColor: isActive ? ACCENT : "#ffffff",
      color: isActive ? "white" : ACCENT,
    };
  };

  const svgProps = {
    "aria-hidden": "true",
    width: "18px",
    height: "18px",
    viewBox: "0 0 34 34",
    xmlns: "http://www.w3.org/2000/svg",
    style: { fill: "currentColor" },
  };

  return (
    <aside
      className="widget widget_product widget_features"
      style={{
        padding: "18px",
        boxSizing: "border-box",
        letterSpacing: "0.2px",
        maxWidth: "100%",
        color: "#1e293b",
        borderRadius: "14px",
        backgroundColor: "#ffffff",
        border: "1px solid #eef0f2",
      }}
    >
      <h2 style={{ fontWeight: 700, fontSize: 17, marginBottom: 16, lineHeight: "22px", color: "#1e293b" }}>Bewertungen</h2>
      <h2
        style={{
          fontWeight: 600,
          fontSize: 13,
          lineHeight: "20px",
          marginBottom: 10,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Dieses Produkt bewerten
      </h2>

      <div
        role="group"
        aria-label={`Die Bewertung ist 0 (von ${starsCount} Sternen).`}
        style={{ display: "flex", flexWrap: "nowrap", gap: "8px", justifyContent: "space-between", boxSizing: "border-box", width: "100%" }}
      >
        {[...Array(starsCount)].map((_, idx) => (
          <button
            key={idx}
            type="button"
            aria-pressed="false"
            tabIndex={0}
            onClick={() => handleClick(idx + 1)}
            title={`Bewertung mit ${idx + 1} Stern${idx + 1 > 1 ? "en" : ""}`}
            style={getButtonStyle(idx)}
            onMouseEnter={() => setHoveredIndex(idx)}
            onMouseLeave={() => setHoveredIndex(null)}
            onFocus={() => setFocusedIndex(idx)}
            onBlur={() => setFocusedIndex(null)}
          >
            <svg {...svgProps}>
              <path d="M16.308,1.765 L20.838,11.417 L31.059,12.979 L23.660,20.562 L25.398,31.212 L16.308,26.187 L7.218,31.212 L8.955,20.562 L1.556,12.979 L11.777,11.417 L16.308,1.765 Z" />
            </svg>
          </button>
        ))}
      </div>
    </aside>
  );
};

export const useProductReviewFlow = (product) => {
  const apiBasePath = process.env.NEXT_PUBLIC_FRONTEND_URL;
  const showPopup = useSafePopup();
  const productId = product?.id;
  const productTitle = product?.attributes?.title || "Unbekanntes Produkt";
  const productImageUrl = useMemo(() => {
    return product?.attributes?.base_image_thumbnail
      ? `${apiBasePath}/images/${product.attributes.base_image_thumbnail}`
      : "/static/img/products/rawento-product-placeholder.webp";
  }, [product, apiBasePath]);

  const [modal, setModal] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginReturnTo, setLoginReturnTo] = useState("/");

  const openLogin = useCallback(() => {
    setLoginReturnTo(window.location.pathname + window.location.search);
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => setLoginOpen(false), []);

  const startReview = useCallback(
    async (rating = null) => {
      const token = typeof window !== "undefined" ? localStorage.getItem("jwt") : null;
      if (!token) {
        openLogin();
        return;
      }
      try {
        const res = await fetch(`${API_URL}/product-reviews/eligibility/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (res.status === 401 || res.status === 403) {
          openLogin();
          return;
        }
        const data = await res.json();
        if (!data?.eligible) {
          const detail =
            data?.reason === "already_reviewed"
              ? "Du hast dieses Produkt bereits bewertet."
              : "Du kannst dieses Produkt bewerten, sobald du es bei uns gekauft hast und die Bestellung geliefert wurde.";
          showPopup({ type: "info", title: "Bewertung nicht möglich", detail, duration: 6000 });
          return;
        }
        setModal({ rating, orderId: data.orderId, nickname: data.nickname || null });
      } catch (e) {
        showPopup({ type: "info", title: "Fehler", detail: "Bitte versuche es später erneut.", duration: 4000 });
      }
    },
    [productId, showPopup, openLogin]
  );

  const reviewModal = (
    <>
      {modal && (
        <ProductReviewModal
          productId={productId}
          orderId={modal.orderId}
          productName={productTitle}
          productImage={productImageUrl}
          initialRating={modal.rating}
          savedNickname={modal.nickname}
          onClose={() => setModal(null)}
          onSubmitted={(result) =>
            showPopup({
              type: "success",
              title: "Danke für deine Bewertung!",
              detail: reviewThanksDetail(result),
              duration: result?.nicknameSaved ? 10000 : 6000,
            })
          }
        />
      )}
      <LoginPromptModal
        isOpen={loginOpen}
        onClose={closeLogin}
        returnTo={loginReturnTo}
        headIcon="star"
        title="Bitte einloggen"
        subtitle="Bewertungen können nur eingeloggte Kunden nach einem Kauf abgeben."
        benefitsTitle="Mit einem kostenlosen Kundenkonto kannst du:"
        benefits={REVIEW_LOGIN_BENEFITS}
        primaryLabel="Anmelden oder registrieren"
        dismissLabel="Abbrechen"
      />
    </>
  );

  return { startReview, reviewModal };
};

export const ProductReviewFlow = ({ product }) => {
  const { startReview, reviewModal } = useProductReviewFlow(product);

  useEffect(() => {
    const handler = (e) => startReview(e?.detail?.rating ?? null);
    window.addEventListener("rw:write-review", handler);
    return () => window.removeEventListener("rw:write-review", handler);
  }, [startReview]);

  return reviewModal;
};

const WidgetProductReview = () => (
  <ProductReview
    onStarClick={(rating) =>
      window.dispatchEvent(new CustomEvent("rw:write-review", { detail: { rating } }))
    }
  />
);

export default WidgetProductReview;